import { dateTimeInUtcOffset, dateTimeToView, formatUtcOffset, plainDate } from "helpers/date";
import { bidStatuses, bidStatusesEnum } from "helpers/enums/bidStatuses";
import { tripSheetAssignmentStatuses } from "helpers/enums/tripSheetAssignmentStatuses";
import { tripSheetFactSources, tripSheetFactSourcesEnum } from "helpers/enums/tripSheetFactSources";
import { tripSheetRouteOperationTypes } from "helpers/enums/tripSheetRouteOperationTypes";
import { tripSheetStatuses, tripSheetStatusesEnum } from "helpers/enums/tripSheetStatuses";
import { paths } from "helpers/paths";
import { enumLabel } from "helpers/system";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { assignmentFromBid, getAssignmentPoints, withAssignmentPoints } from "./model";
import { CouplingsSection, DriverShiftsSection, TechnicalInspectionsSection } from "./operationSections";

const TripSheetSection = ({ title, children }) => (
    <section className="trip-sheet-form__section">
        <header className="trip-sheet-form__section-header">
            <h2 className="trip-sheet-form__section-title">{title}</h2>
        </header>
        <div className="trip-sheet-form__section-body">{children}</div>
    </section>
);
const ReadonlyRow = ({ label, value }) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    return (
        <div className="row">
            <div className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">{label}</div>
            <div className="col-sm-8 col-form-label col-form-label-sm text-muted">{value}</div>
        </div>
    );
};
const getAssignmentDateInOrganizationTimezone = (value, organizationTimezone) => {
    if (!value) {
        return "";
    }
    const organizationUtcOffset = formatUtcOffset(organizationTimezone);
    return organizationUtcOffset ? dateTimeInUtcOffset(value, organizationUtcOffset) : value;
};
const formatAssignmentDate = (value, organizationTimezone) => {
    const date = getAssignmentDateInOrganizationTimezone(value, organizationTimezone);
    return date ? dateTimeToView(date) : "";
};
const renderAssignmentValue = (value) => {
    if (value || value === 0) {
        return (
            <span
                style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {value}
            </span>
        );
    }
    return <span className="text-muted">Не указано</span>;
};
const renderAssignmentDate = (value, organizationTimezone) => {
    if (!value) {
        return <span className="text-muted">Не указано</span>;
    }
    const organizationUtcOffset = formatUtcOffset(organizationTimezone);
    return (
        <span className="text-nowrap">
            {formatAssignmentDate(value, organizationTimezone)}
            {organizationUtcOffset && <span className="text-muted ml-1">{organizationUtcOffset}</span>}
        </span>
    );
};
const AssignmentReadonlyField = ({ label, value, wide, isDate, organizationTimezone }) => (
    <div className={wide ? "col-md-12 mb-2" : "col-sm-6 col-md-4 mb-2"}>
        <div className="small text-muted mb-1">{label}</div>
        <div>{isDate ? renderAssignmentDate(value, organizationTimezone) : renderAssignmentValue(value)}</div>
    </div>
);
const optionsWithCurrentValue = (options, value) =>
    value && !options.some((option) => option.value === value)
        ? [
              {
                  value,
                  label: value,
              },
              ...options,
          ]
        : options;
const RoutePointsEditor = ({
    row,
    assignmentIndex,
    onChange,
    canEditPlan,
    canEditFacts,
    organizationTimezone,
    renderField,
    renderSelect,
    renderCheckbox,
    renderDateField,
}) => {
    const points = getAssignmentPoints(row);
    const updatePoint = (pointIndex, field, value) => {
        const nextPoints = [...points];
        nextPoints[pointIndex] = { ...nextPoints[pointIndex], [field]: value };
        onChange(assignmentIndex, withAssignmentPoints(row, nextPoints));
    };
    const updatePointDate = (pointIndex, field, value) => {
        const nextPoints = [...points];
        let nextPoint = { ...nextPoints[pointIndex], [field]: plainDate(value) };
        if (field.startsWith("actual")) {
            const source = nextPoint.factSource;
            if ([tripSheetFactSourcesEnum.Bid, tripSheetFactSourcesEnum.Telematics].includes(source)) {
                nextPoint = { ...nextPoint, factSource: tripSheetFactSourcesEnum.Corrected };
            } else if (!source && value) {
                nextPoint = { ...nextPoint, factSource: tripSheetFactSourcesEnum.Manual };
            }
        }
        nextPoints[pointIndex] = nextPoint;
        onChange(assignmentIndex, withAssignmentPoints(row, nextPoints));
    };
    return (
        <div className="mt-3 border-top pt-2">
            <div className="font-weight-bold small mb-2">Маршрутные точки ({points.length})</div>
            {points.map((point, pointIndex) => (
                <div className="border rounded p-2 mb-2" key={point.id || point.bidPointId || pointIndex}>
                    <div>
                        {renderField({
                            label: "Порядок",
                            value: point.sequenceNumber || pointIndex + 1,
                            disabled: !canEditPlan,
                            inputProps: {
                                name: `pointSequenceNumber_${assignmentIndex}_${pointIndex}`,
                                type: "number",
                                min: 1,
                            },
                            onChange: ({ value }) => updatePoint(pointIndex, "sequenceNumber", value),
                        })}
                        {renderSelect({
                            label: "Тип операции",
                            value: point.operationType || "",
                            options: optionsWithCurrentValue(tripSheetRouteOperationTypes, point.operationType),
                            disabled: !canEditPlan,
                            inputProps: {
                                name: `pointOperationType_${assignmentIndex}_${pointIndex}`,
                            },
                            onChange: ({ value }) => updatePoint(pointIndex, "operationType", value),
                        })}
                        {renderSelect({
                            label: "Источник факта",
                            value: point.factSource || "",
                            options: optionsWithCurrentValue(tripSheetFactSources, point.factSource),
                            disabled: !canEditFacts,
                            inputProps: {
                                name: `pointFactSource_${assignmentIndex}_${pointIndex}`,
                            },
                            onChange: ({ value }) => updatePoint(pointIndex, "factSource", value),
                        })}
                        {renderField({
                            label: "Адрес",
                            textarea: true,
                            value: point.address || "",
                            disabled: !canEditPlan,
                            inputProps: {
                                name: `pointAddress_${assignmentIndex}_${pointIndex}`,
                            },
                            onChange: ({ value }) => updatePoint(pointIndex, "address", value),
                        })}
                        {[
                            ["Плановое прибытие", "plannedArrivalAt", canEditPlan],
                            ["Плановое убытие", "plannedDepartureAt", canEditPlan],
                            ["Фактическое прибытие", "actualArrivalAt", canEditFacts],
                            ["Начало операции", "actualOperationStartedAt", canEditFacts],
                            ["Окончание операции", "actualOperationEndedAt", canEditFacts],
                            ["Фактическое убытие", "actualDepartureAt", canEditFacts],
                        ].map(([label, field, editable]) =>
                            renderDateField({
                                key: field,
                                label: label,
                                inPortal: true,
                                value: plainDate(
                                    getAssignmentDateInOrganizationTimezone(point[field], organizationTimezone),
                                ),
                                disabled: !editable,
                                inputProps: {
                                    name: `${field}_${assignmentIndex}_${pointIndex}`,
                                    type: "datetime",
                                },
                                onChange: ({ value }) => updatePointDate(pointIndex, field, value),
                            }),
                        )}
                        {renderField({
                            label: "Смена водителя",
                            value: point.driverShiftId ?? "",
                            disabled: !canEditFacts,
                            inputProps: {
                                name: `pointDriverShiftId_${assignmentIndex}_${pointIndex}`,
                                type: "number",
                                min: 1,
                            },
                            onChange: ({ value }) => updatePoint(pointIndex, "driverShiftId", value),
                        })}
                        {renderCheckbox({
                            label: "Подтверждено диспетчером",
                            value: !!point.isDispatcherConfirmed,
                            disabled: !canEditFacts,
                            inputProps: {
                                name: `pointDispatcherConfirmed_${assignmentIndex}_${pointIndex}`,
                            },
                            onChange: ({ value }) => updatePoint(pointIndex, "isDispatcherConfirmed", value),
                        })}
                        {renderField({
                            label: "Комментарий",
                            textarea: true,
                            value: point.comment || "",
                            disabled: !canEditFacts,
                            inputProps: {
                                name: `pointComment_${assignmentIndex}_${pointIndex}`,
                            },
                            onChange: ({ value }) => updatePoint(pointIndex, "comment", value),
                        })}
                        {renderField({
                            label: "Причина ручной корректировки факта",
                            textarea: true,
                            value: point.correctionReason || "",
                            disabled: !canEditFacts,
                            inputProps: {
                                name: `pointCorrectionReason_${assignmentIndex}_${pointIndex}`,
                            },
                            onChange: ({ value }) => updatePoint(pointIndex, "correctionReason", value),
                        })}
                    </div>
                    {canEditPlan && (
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-xs"
                            onClick={() =>
                                onChange(
                                    assignmentIndex,
                                    withAssignmentPoints(
                                        row,
                                        points.filter((_, index) => index !== pointIndex),
                                    ),
                                )
                            }
                        >
                            Удалить точку
                        </button>
                    )}
                </div>
            ))}
            {points.length === 0 && <div className="small text-muted mb-2">Маршрутные точки не заполнены.</div>}
            {canEditPlan && (
                <button
                    type="button"
                    className="btn btn-secondary btn-xs"
                    onClick={() =>
                        onChange(
                            assignmentIndex,
                            withAssignmentPoints(row, [
                                ...points,
                                {
                                    sequenceNumber: points.length + 1,
                                },
                            ]),
                        )
                    }
                >
                    Добавить маршрутную точку
                </button>
            )}
        </div>
    );
};
const AssignmentDetails = ({
    row,
    index,
    onChange,
    canEditPlan,
    canEditFacts,
    organizationTimezone,
    renderField,
    renderSelect,
    renderCheckbox,
    renderDateField,
}) => {
    const organizationUtcOffset = formatUtcOffset(organizationTimezone);
    const handleChange = (field, value) => {
        onChange(index, { ...row, [field]: value });
    };
    const handleDateChange = (field, value) => {
        handleChange(field, plainDate(value));
    };
    const handleActualDistanceChange = (value) => {
        let nextRow = { ...row, actualDistance: value };
        const source = row.actualDistanceSource;
        if ([tripSheetFactSourcesEnum.Bid, tripSheetFactSourcesEnum.Telematics].includes(source)) {
            nextRow = { ...nextRow, actualDistanceSource: tripSheetFactSourcesEnum.Corrected };
        } else if (!source && value !== "") {
            nextRow = { ...nextRow, actualDistanceSource: tripSheetFactSourcesEnum.Manual };
        }
        onChange(index, nextRow);
    };
    if (!canEditPlan && !canEditFacts) {
        return (
            <div className="border rounded p-3">
                <div className="row">
                    <AssignmentReadonlyField label="Контрагент" value={row.counterpartyDetails} wide />
                    <AssignmentReadonlyField label="Адрес погрузки" value={row.loadingAddress} wide />
                    <AssignmentReadonlyField
                        label="Время прибытия"
                        value={row.arrivalTime}
                        isDate
                        organizationTimezone={organizationTimezone}
                    />
                    <AssignmentReadonlyField label="Адрес разгрузки" value={row.unloadingAddress} wide />
                    <AssignmentReadonlyField
                        label="Время убытия"
                        value={row.departureTime}
                        isDate
                        organizationTimezone={organizationTimezone}
                    />
                    <AssignmentReadonlyField label="Груз" value={row.cargoDetails} wide />
                    <AssignmentReadonlyField label="Кол-во ездок" value={row.tripCount} />
                    <AssignmentReadonlyField label="Расстояние, км" value={row.distance} />
                    <AssignmentReadonlyField label="Вес, т" value={row.plannedWeight ?? row.weight} />
                </div>
                <RoutePointsEditor
                    row={row}
                    assignmentIndex={index}
                    onChange={onChange}
                    canEditPlan={false}
                    canEditFacts={false}
                    organizationTimezone={organizationTimezone}
                    renderField={renderField}
                    renderSelect={renderSelect}
                    renderCheckbox={renderCheckbox}
                    renderDateField={renderDateField}
                />
            </div>
        );
    }
    return (
        <div className="border rounded p-3">
            <div>
                {renderField({
                    label: "Контрагент",
                    value: row.counterpartyDetails || "",
                    disabled: !canEditPlan,
                    inputProps: {
                        name: `assignmentCounterpartyDetails_${index}`,
                    },
                    onChange: ({ value }) => handleChange("counterpartyDetails", value),
                })}
                {renderField({
                    label: "Адрес погрузки",
                    textarea: true,
                    value: row.loadingAddress || "",
                    disabled: !canEditPlan,
                    inputProps: {
                        name: `assignmentLoadingAddress_${index}`,
                    },
                    onChange: ({ value }) => handleChange("loadingAddress", value),
                })}
                {renderDateField({
                    label: "Время прибытия",
                    inPortal: true,
                    additionalText: organizationUtcOffset,
                    value: plainDate(getAssignmentDateInOrganizationTimezone(row.arrivalTime, organizationTimezone)),
                    disabled: !canEditPlan,
                    inputProps: {
                        name: `assignmentArrivalTime_${index}`,
                        type: "datetime",
                    },
                    onChange: ({ value }) => handleDateChange("arrivalTime", value),
                })}
                {renderField({
                    label: "Адрес разгрузки",
                    textarea: true,
                    value: row.unloadingAddress || "",
                    disabled: !canEditPlan,
                    inputProps: {
                        name: `assignmentUnloadingAddress_${index}`,
                    },
                    onChange: ({ value }) => handleChange("unloadingAddress", value),
                })}
                {renderDateField({
                    label: "Время убытия",
                    inPortal: true,
                    additionalText: organizationUtcOffset,
                    value: plainDate(getAssignmentDateInOrganizationTimezone(row.departureTime, organizationTimezone)),
                    disabled: !canEditPlan,
                    inputProps: {
                        name: `assignmentDepartureTime_${index}`,
                        type: "datetime",
                    },
                    onChange: ({ value }) => handleDateChange("departureTime", value),
                })}
                {renderField({
                    label: "Груз",
                    textarea: true,
                    value: row.cargoDetails || "",
                    disabled: !canEditPlan,
                    inputProps: {
                        name: `assignmentCargoDetails_${index}`,
                    },
                    onChange: ({ value }) => handleChange("cargoDetails", value),
                })}
                {renderField({
                    label: "Кол-во ездок",
                    value: row.tripCount || "",
                    disabled: !canEditPlan,
                    inputProps: {
                        name: `assignmentTripCount_${index}`,
                        type: "number",
                        min: 1,
                    },
                    onChange: ({ value }) => handleChange("tripCount", value),
                })}
                {renderField({
                    label: "Расстояние, км",
                    value: row.distance || "",
                    disabled: !canEditPlan,
                    inputProps: {
                        name: `assignmentDistance_${index}`,
                        type: "number",
                        min: 0,
                        step: "0.1",
                    },
                    onChange: ({ value }) => handleChange("distance", value),
                })}
                {renderField({
                    label: "Вес, т",
                    value: row.plannedWeight ?? row.weight ?? "",
                    disabled: !canEditPlan,
                    inputProps: {
                        name: `assignmentPlannedWeight_${index}`,
                        type: "number",
                        min: 0,
                        step: "0.01",
                    },
                    onChange: ({ value }) => handleChange("plannedWeight", value),
                })}
                {renderSelect({
                    label: "Статус выполнения",
                    value: row.status || "",
                    options: optionsWithCurrentValue(tripSheetAssignmentStatuses, row.status),
                    disabled: !canEditFacts,
                    inputProps: {
                        name: `assignmentStatus_${index}`,
                    },
                    onChange: ({ value }) => handleChange("status", value || null),
                })}
                {renderField({
                    label: "Фактическое количество ездок",
                    value: row.actualTripCount ?? "",
                    disabled: !canEditFacts,
                    inputProps: {
                        name: `assignmentActualTripCount_${index}`,
                        type: "number",
                        min: 0,
                    },
                    onChange: ({ value }) => handleChange("actualTripCount", value),
                })}
                {renderField({
                    label: "Фактическое расстояние, км",
                    value: row.actualDistance ?? "",
                    disabled: !canEditFacts,
                    inputProps: {
                        name: `assignmentActualDistance_${index}`,
                        type: "number",
                        min: 0,
                        step: "0.1",
                    },
                    onChange: ({ value }) => handleActualDistanceChange(value),
                })}
                {renderSelect({
                    label: "Источник фактического расстояния",
                    value: row.actualDistanceSource || "",
                    options: optionsWithCurrentValue(tripSheetFactSources, row.actualDistanceSource),
                    disabled: !canEditFacts,
                    inputProps: {
                        name: `assignmentActualDistanceSource_${index}`,
                    },
                    onChange: ({ value }) => handleChange("actualDistanceSource", value || null),
                })}
                {renderField({
                    label: "Участвовавшие смены (ID через запятую)",
                    value: (row.driverShiftIds || []).join(", "),
                    disabled: !canEditFacts,
                    inputProps: {
                        name: `assignmentDriverShiftIds_${index}`,
                    },
                    onChange: ({ value }) =>
                        handleChange(
                            "driverShiftIds",
                            `${value || ""}`
                                .split(",")
                                .map((shiftId) => Number(shiftId.trim()))
                                .filter((shiftId) => Number.isFinite(shiftId) && shiftId > 0),
                        ),
                })}
                {renderField({
                    label: "Причина невыполнения / отмены",
                    textarea: true,
                    value: row.nonCompletionReason || "",
                    disabled: !canEditFacts,
                    inputProps: {
                        name: `assignmentNonCompletionReason_${index}`,
                    },
                    onChange: ({ value }) => handleChange("nonCompletionReason", value),
                })}
                {renderField({
                    label: "Причина ручной корректировки фактов",
                    textarea: true,
                    value: row.actualDistanceComment || "",
                    disabled: !canEditFacts,
                    inputProps: {
                        name: `assignmentActualDistanceComment_${index}`,
                    },
                    onChange: ({ value }) => handleChange("actualDistanceComment", value),
                })}
            </div>
            <RoutePointsEditor
                row={row}
                assignmentIndex={index}
                onChange={onChange}
                canEditPlan={canEditPlan}
                canEditFacts={canEditFacts}
                organizationTimezone={organizationTimezone}
                renderField={renderField}
                renderSelect={renderSelect}
                renderCheckbox={renderCheckbox}
                renderDateField={renderDateField}
            />
        </div>
    );
};
const AssignmentRouteSummary = ({ row }) => (
    <div>
        <div>
            <span className="text-muted">Погрузка: </span>
            {renderAssignmentValue(row.loadingAddress)}
        </div>
        <div className="mt-1">
            <span className="text-muted">Разгрузка: </span>
            {renderAssignmentValue(row.unloadingAddress)}
        </div>
    </div>
);
const AssignmentMetrics = ({ row }) => (
    <div>
        <span className="text-muted">Ездок: </span>
        {row.tripCount || "-"}
        <span className="text-muted ml-2">км: </span>
        {row.distance || "-"}
        <span className="text-muted ml-2">т: </span>
        {row.weight || "-"}
    </div>
);
const AssignmentRow = ({
    row,
    index,
    onChange,
    onRemove,
    canEditPlan,
    canEditFacts,
    isOpened,
    onToggle,
    organizationTimezone,
    renderField,
    renderSelect,
    renderCheckbox,
    renderDateField,
}) => {
    return (
        <>
            <tr>
                <td
                    className="text-center align-middle"
                    style={{
                        width: 36,
                    }}
                >
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onToggle}
                        title={isOpened ? "Свернуть" : "Развернуть"}
                        aria-label={isOpened ? "Свернуть задание" : "Развернуть задание"}
                    >
                        <i className={isOpened ? "icon-uEA61-angle-down" : "icon-uEAAE-angle-right-solid"} />
                    </button>
                </td>
                <td className="text-nowrap">{row.id || <span className="text-muted">Не сохранено</span>}</td>
                <td>{renderAssignmentValue(row.counterpartyDetails)}</td>
                <td>
                    <AssignmentRouteSummary row={row} />
                </td>
                <td>{renderAssignmentDate(row.arrivalTime, organizationTimezone)}</td>
                <td>{renderAssignmentDate(row.departureTime, organizationTimezone)}</td>
                <td>{renderAssignmentValue(row.cargoDetails)}</td>
                <td>
                    <AssignmentMetrics row={row} />
                </td>
                <td
                    className="text-center align-middle"
                    style={{
                        width: 52,
                    }}
                >
                    {canEditPlan && (
                        <button
                            type="button"
                            className="btn btn-xs btn-outline-danger"
                            aria-label={`Удалить задание ${index + 1}`}
                            onClick={() => onRemove(index)}
                        >
                            ✕
                        </button>
                    )}
                </td>
            </tr>
            {isOpened && (
                <tr>
                    <td colSpan={9}>
                        <AssignmentDetails
                            row={row}
                            index={index}
                            onChange={onChange}
                            canEditPlan={canEditPlan}
                            canEditFacts={canEditFacts}
                            organizationTimezone={organizationTimezone}
                            renderField={renderField}
                            renderSelect={renderSelect}
                            renderCheckbox={renderCheckbox}
                            renderDateField={renderDateField}
                        />
                    </td>
                </tr>
            )}
        </>
    );
};
const bidStatusBadgeClass = {
    [bidStatusesEnum.New]: "badge-light",
    [bidStatusesEnum.Planned]: "badge-primary",
    [bidStatusesEnum.Started]: "badge-info",
    [bidStatusesEnum.Canceled]: "badge-secondary",
    [bidStatusesEnum.Done]: "badge-success",
};
const AssignmentsSection = ({
    assignments,
    onAssignmentsChange,
    canEditPlan,
    canEditFacts,
    source,
    data,
    status,
    reload,
    onChangeReasonRequired,
    organizationTimezone,
    renderField,
    renderSelect,
    renderCheckbox,
    renderDateField,
}) => {
    const [loadingBids, setLoadingBids] = useState(false);
    const [availableBids, setAvailableBids] = useState([]);
    const [showBidPicker, setShowBidPicker] = useState(false);
    const [openedRows, setOpenedRows] = useState({});
    const [bidConflicts, setBidConflicts] = useState([]);
    const [syncReason, setSyncReason] = useState("");
    const [syncError, setSyncError] = useState("");
    const [isSyncing, setIsSyncing] = useState(false);
    const { carId, driverId, validFrom, validTo } = data;
    const assignmentPeriod = {
        from: getAssignmentDateInOrganizationTimezone(validFrom, organizationTimezone),
        to: getAssignmentDateInOrganizationTimezone(validTo, organizationTimezone),
    };
    useEffect(() => {
        if (!showBidPicker || !carId) {
            return;
        }
        setLoadingBids(true);
        source
            .getBidsForAssignment({
                carId,
                driverId,
                from: assignmentPeriod.from || "",
                to: assignmentPeriod.to || "",
            })
            .then((bids) => {
                setAvailableBids(bids || []);
            })
            .catch(() => setAvailableBids([]))
            .finally(() => setLoadingBids(false));
    }, [showBidPicker, carId, driverId, assignmentPeriod.from, assignmentPeriod.to, source]);
    const addEmptyRow = () => {
        const nextIndex = assignments.length;
        onAssignmentsChange([
            ...assignments,
            {
                counterpartyDetails: "",
                loadingAddress: "",
                arrivalTime: "",
                unloadingAddress: "",
                departureTime: "",
                cargoDetails: "",
                tripCount: 1,
                distance: "",
                weight: "",
                routePoints: [],
            },
        ]);
        setOpenedRows((prev) => ({
            ...prev,
            [nextIndex]: true,
        }));
    };
    const updateRow = (index, updatedRow) => {
        const next = [...assignments];
        next[index] = updatedRow;
        onAssignmentsChange(next);
    };
    const removeRow = (index) => {
        onAssignmentsChange(assignments.filter((_, i) => i !== index));
        setOpenedRows((prev) =>
            Object.keys(prev).reduce((memo, key) => {
                const rowIndex = Number(key);
                if (!prev[key] || rowIndex === index) {
                    return memo;
                }
                memo[rowIndex > index ? rowIndex - 1 : rowIndex] = true;
                return memo;
            }, {}),
        );
    };
    const toggleRow = (index) => {
        setOpenedRows((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };
    const loadBids = () => {
        if (!carId || showBidPicker) {
            return;
        }
        setLoadingBids(true);
        setShowBidPicker(true);
    };
    const appendBid = (bid) => {
        const nextIndex = assignments.length;
        const newRow = assignmentFromBid(bid);
        onAssignmentsChange([...assignments, newRow]);
        setOpenedRows((prev) => ({
            ...prev,
            [nextIndex]: true,
        }));
    };
    const addBid = (bid) => {
        if (assignments.some((assignment) => String(assignment.bidId) === String(bid.id))) {
            return;
        }
        source
            .getBidConflicts({
                id: data.id || 0,
                bidIds: [bid.id],
            })
            .then((conflicts) => {
                const nextConflicts = conflicts || [];
                setBidConflicts(nextConflicts);
                if (nextConflicts.length > 0) {
                    onChangeReasonRequired(
                        "Заявка уже есть в другом незакрытом путевом листе. Укажите причину повторного включения в поле причин перед сохранением.",
                    );
                }
                appendBid(bid);
            })
            .catch(() => appendBid(bid));
    };
    const synchronize = ({ updatePlan, updateFacts }) => {
        if (!data.id || isSyncing) {
            return;
        }
        if (status === tripSheetStatusesEnum.Issued && updateFacts && !syncReason.trim()) {
            setSyncError("Для синхронизации фактов выданного листа укажите причину");
            return;
        }
        setIsSyncing(true);
        setSyncError("");
        source
            .syncAssignments({
                id: data.id,
                assignmentIds: [],
                updatePlan,
                updateFacts,
                reason: syncReason.trim() || null,
            })
            .then((result) => result && reload())
            .finally(() => setIsSyncing(false));
    };
    return (
        <div>
            <div className="table-responsive">
                <table className="table table-sm table-bordered">
                    <thead className="thead-light">
                        <tr>
                            <th
                                style={{
                                    width: 36,
                                }}
                            />
                            <th className="text-nowrap">ID задания</th>
                            <th>Контрагент</th>
                            <th>Маршрут</th>
                            <th>Время прибытия</th>
                            <th>Время убытия</th>
                            <th>Груз</th>
                            <th>Показатели</th>
                            <th
                                style={{
                                    width: 52,
                                }}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {assignments.map((row, i) => (
                            <AssignmentRow
                                key={i}
                                row={row}
                                index={i}
                                onChange={updateRow}
                                onRemove={removeRow}
                                canEditPlan={canEditPlan}
                                canEditFacts={canEditFacts}
                                isOpened={!!openedRows[i]}
                                onToggle={() => toggleRow(i)}
                                organizationTimezone={organizationTimezone}
                                renderField={renderField}
                                renderSelect={renderSelect}
                                renderCheckbox={renderCheckbox}
                                renderDateField={renderDateField}
                            />
                        ))}
                        {assignments.length === 0 && (
                            <tr>
                                <td colSpan={9} className="text-center text-muted py-2">
                                    Нет заданий
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {canEditPlan && (
                <div className="d-flex mt-2">
                    <button type="button" className="inline-btn inline-btn--plus mr-2" onClick={addEmptyRow}>
                        Добавить вручную
                    </button>
                    <button
                        type="button"
                        className={`inline-btn inline-btn--plus ${loadingBids ? "disabled" : ""}`}
                        onClick={loadingBids ? undefined : loadBids}
                        disabled={loadingBids}
                    >
                        {loadingBids ? "Загрузка..." : "Добавить из заявок"}
                    </button>
                </div>
            )}

            {bidConflicts.length > 0 && (
                <div className="alert alert-warning mt-3 mb-2">
                    <div className="font-weight-bold">Заявки уже включены в незакрытые путевые листы</div>
                    {bidConflicts.map((conflict) => (
                        <div key={`${conflict.bidId}_${conflict.tripSheetId}`}>
                            Заявка #{conflict.bidId}: лист{" "}
                            <Link to={paths.tripSheetsShow(conflict.tripSheetId)}>
                                {conflict.tripSheetNumber || `#${conflict.tripSheetId}`}
                            </Link>{" "}
                            ({enumLabel(tripSheetStatuses, conflict.status) || conflict.status})
                        </div>
                    ))}
                    <div className="small mt-1">
                        Добавление разрешено после заполнения осмысленной причины повторного включения.
                    </div>
                </div>
            )}

            {data.id && (canEditPlan || canEditFacts) && (
                <div className="border rounded p-3 mt-3">
                    <div className="font-weight-bold mb-2">Синхронизация с заявками</div>
                    {renderField({
                        label: "Причина синхронизации",
                        textarea: true,
                        value: syncReason,
                        disabled: false,
                        inputProps: {
                            name: "assignmentSyncReason",
                        },
                        onChange: ({ value }) => {
                            setSyncReason(value);
                            setSyncError("");
                        },
                    })}
                    {syncError && <div className="text-danger small mb-2">{syncError}</div>}
                    {status === tripSheetStatusesEnum.New && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm mr-2"
                            disabled={isSyncing}
                            onClick={() =>
                                synchronize({
                                    updatePlan: true,
                                    updateFacts: false,
                                })
                            }
                        >
                            Обновить план из заявок
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={isSyncing}
                        onClick={() =>
                            synchronize({
                                updatePlan: false,
                                updateFacts: true,
                            })
                        }
                    >
                        Обновить факты из заявок
                    </button>
                </div>
            )}

            {showBidPicker && (
                <div className="mt-3 border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Выберите заявки</strong>
                        <button
                            type="button"
                            className="btn"
                            aria-label="Закрыть выбор заявок"
                            onClick={() => setShowBidPicker(false)}
                        >
                            ✕
                        </button>
                    </div>
                    {availableBids.length === 0 ? (
                        <div className="text-muted">Нет доступных заявок для выбранного ТС и периода</div>
                    ) : (
                        <table className="table table-sm table-hover">
                            <thead className="thead-light">
                                <tr>
                                    <th>ID</th>
                                    <th>Статус</th>
                                    <th>Откуда</th>
                                    <th>Куда</th>
                                    <th>Контрагент</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {availableBids.map((bid) => (
                                    <tr key={bid.id}>
                                        <td>{bid.id}</td>
                                        <td>
                                            <span
                                                className={`badge ${bidStatusBadgeClass[bid.status] || "badge-light"}`}
                                            >
                                                {enumLabel(bidStatuses, bid.status)}
                                            </span>
                                        </td>
                                        <td>{bid.firstBidPoint?.address}</td>
                                        <td>{bid.lastBidPoint?.address}</td>
                                        <td>{bid.counterparty?.details}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-xs btn-secondary"
                                                disabled={assignments.some(
                                                    (assignment) => String(assignment.bidId) === String(bid.id),
                                                )}
                                                onClick={() => {
                                                    addBid(bid);
                                                }}
                                            >
                                                {assignments.some(
                                                    (assignment) => String(assignment.bidId) === String(bid.id),
                                                )
                                                    ? "Добавлена"
                                                    : "Добавить"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};
const Markup = ({
    renderField,
    renderSelect,
    renderDateField,
    renderCheckbox,
    data,
    assignments,
    onAssignmentsChange,
    driverShifts,
    onDriverShiftsChange,
    technicalInspections,
    onTechnicalInspectionsChange,
    trailerPeriods,
    canEditPlan,
    canEditFacts,
    status,
    source,
    reload,
    onChangeReasonRequired,
    Button,
    organizationTimezone,
    // config fields
    legalPersonId,
    validFrom,
    validTo,
    documentDate,
    communicationType,
    transportationKind,
    dispatcherComment,
    noAssignmentReason,
    changeReason,
    driverId,
    secondaryDriverId,
    isCrew,
    carId,
    trailerId,
    plannedDepartureAt,
    actualDepartureAt,
    odometerValueOnDeparture,
    plannedReturnAt,
    actualReturnAt,
    odometerValueOnReturn,
    departureOdometerCorrectionReason,
    returnOdometerCorrectionReason,
    mileageDiscrepancyReason,
    issuedLiters,
    issuedCorrectionReason,
    balanceDepartureLiters,
    balanceDepartureCorrectionReason,
    refilledOnRouteLiters,
    refilledOnRouteCorrectionReason,
    balanceReturnLiters,
    balanceReturnCorrectionReason,
    normChangeCoefficient,
    mechanicName,
    dispatcherName,
    taxerName,
    controllerName,
    preTripInspectionAt,
    medicalWorkerName,
    medicalExamAt,
    medicalMarksConfirmedOnPaper,
    technicalInspectionConfirmedOnPaper,
}) => {
    const fuelValues = [balanceDepartureLiters, issuedLiters, refilledOnRouteLiters, balanceReturnLiters].map(
        ({ inputProps }) => data[inputProps.name],
    );
    const canCalculateFuelConsumption = fuelValues.every(
        (value) => value !== "" && value !== null && value !== undefined && Number.isFinite(Number(value)),
    );
    const fuelConsumption = canCalculateFuelConsumption
        ? Number(fuelValues[0]) + Number(fuelValues[1]) + Number(fuelValues[2]) - Number(fuelValues[3])
        : null;
    return (
        <div className="trip-sheet-form">
            <TripSheetSection title="Основное">
                {status === tripSheetStatusesEnum.Closed && (
                    <div className="alert alert-info py-2">
                        Закрытый путевой лист неизменяем. Для исправления создайте отдельную корректировочную версию.
                    </div>
                )}
                {(data.documentVersion || data.correctedTripSheetId || data.supersededByTripSheetId) && (
                    <div className="mb-2">
                        <ReadonlyRow label="Версия документа" value={data.documentVersion || 1} />
                        <ReadonlyRow
                            label="Корректирует путевой лист"
                            value={
                                data.correctedTripSheetId && (
                                    <Link to={paths.tripSheetsShow(data.correctedTripSheetId)}>
                                        #{data.correctedTripSheetId}
                                    </Link>
                                )
                            }
                        />
                        <ReadonlyRow
                            label="Заменён корректировочной версией"
                            value={
                                data.supersededByTripSheetId && (
                                    <Link to={paths.tripSheetsShow(data.supersededByTripSheetId)}>
                                        #{data.supersededByTripSheetId}
                                    </Link>
                                )
                            }
                        />
                    </div>
                )}
                {canEditPlan || data.legalPersonId ? (
                    renderSelect(legalPersonId)
                ) : (
                    <div className="mb-3">
                        <ReadonlyRow label="Юридическое лицо" value={data.organizationFullName} />
                        <ReadonlyRow label="Адрес организации" value={data.organizationAddress} />
                        <ReadonlyRow label="Телефон организации" value={data.organizationPhoneNumber} />
                        <ReadonlyRow label="ОГРН" value={data.organizationOgrn} />
                        <ReadonlyRow label="ОКПО" value={data.organizationOkpo} />
                    </div>
                )}
                {renderDateField(validFrom)}
                {renderDateField(validTo)}
                {renderDateField(documentDate)}
                {renderSelect(communicationType)}
                {renderField(transportationKind)}
                {renderField(dispatcherComment)}
                {renderField(noAssignmentReason)}
                {renderField(changeReason)}
            </TripSheetSection>

            <TripSheetSection title="Задания водителю">
                <AssignmentsSection
                    assignments={assignments}
                    onAssignmentsChange={onAssignmentsChange}
                    canEditPlan={canEditPlan}
                    canEditFacts={canEditFacts}
                    source={source}
                    data={data}
                    status={status}
                    reload={reload}
                    onChangeReasonRequired={onChangeReasonRequired}
                    organizationTimezone={organizationTimezone}
                    renderField={renderField}
                    renderSelect={renderSelect}
                    renderCheckbox={renderCheckbox}
                    renderDateField={renderDateField}
                />
            </TripSheetSection>

            <TripSheetSection title="Сцепка">
                {renderSelect(driverId)}

                {/* Данные водителя 1 (readonly) */}
                {data.driverLicenseNumber && (
                    <div className="row form-group">
                        <div className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right">
                            ВУ водителя 1
                        </div>
                        <div className="col-sm-8 col-md-9 col-form-label col-form-label-sm text-muted">
                            {data.driverLicenseNumber}
                        </div>
                    </div>
                )}
                {data.driverSnils && (
                    <div className="row form-group">
                        <div className="col-sm-4 col-form-label col-form-label-sm text-md-right text-muted">
                            СНИЛС водителя 1
                        </div>
                        <div className="col-sm-8 col-form-label col-form-label-sm text-muted">{data.driverSnils}</div>
                    </div>
                )}

                {renderSelect(secondaryDriverId)}
                {renderCheckbox(isCrew)}

                {/* Данные водителя 2 (readonly) */}
                {data.secondaryDriverLicenseNumber && (
                    <div className="row form-group">
                        <div className="col-sm-4 col-form-label col-form-label-sm text-md-right text-muted">
                            ВУ водителя 2
                        </div>
                        <div className="col-sm-8 col-form-label col-form-label-sm text-muted">
                            {data.secondaryDriverLicenseNumber}
                        </div>
                    </div>
                )}
                {data.secondaryDriverSnils && (
                    <div className="row form-group">
                        <div className="col-sm-4 col-form-label col-form-label-sm text-md-right text-muted">
                            СНИЛС водителя 2
                        </div>
                        <div className="col-sm-8 col-form-label col-form-label-sm text-muted">
                            {data.secondaryDriverSnils}
                        </div>
                    </div>
                )}

                {renderSelect(carId)}

                {/* Данные ТС (readonly) */}
                {(data.carBrand || data.carModel) && (
                    <div className="row form-group">
                        <div className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right">
                            Марка/модель ТС
                        </div>
                        <div className="col-sm-8 col-md-9 col-form-label col-form-label-sm text-muted">
                            {[data.carBrand, data.carModel].filter(Boolean).join(" ")}
                        </div>
                    </div>
                )}

                {renderSelect(trailerId)}

                {/* Данные прицепа (readonly) */}
                {(data.trailerBrand || data.trailerModel) && (
                    <div className="row form-group">
                        <div className="col-sm-4 col-form-label col-form-label-sm text-md-right text-muted">
                            Марка/модель прицепа
                        </div>
                        <div className="col-sm-8 col-form-label col-form-label-sm text-muted">
                            {[data.trailerBrand, data.trailerModel].filter(Boolean).join(" ")}
                        </div>
                    </div>
                )}
            </TripSheetSection>

            <TripSheetSection title="Смены водителей">
                <DriverShiftsSection
                    shifts={driverShifts}
                    assignments={assignments}
                    onChange={onDriverShiftsChange}
                    canEditPlan={canEditPlan}
                    canEditFacts={canEditFacts}
                    renderField={renderField}
                    renderSelect={renderSelect}
                    renderCheckbox={renderCheckbox}
                    renderDateField={renderDateField}
                />
            </TripSheetSection>

            <TripSheetSection title="Сцепки и история состава ТС">
                <CouplingsSection
                    data={data}
                    source={source}
                    status={status}
                    canEditPlan={canEditPlan}
                    canEditFacts={canEditFacts}
                    trailerPeriods={trailerPeriods}
                    reload={reload}
                    suggestionFrom={getAssignmentDateInOrganizationTimezone(data.validFrom, organizationTimezone)}
                    suggestionTo={getAssignmentDateInOrganizationTimezone(data.validTo, organizationTimezone)}
                    formatDate={(value) => formatAssignmentDate(value, organizationTimezone)}
                    renderField={renderField}
                    renderCheckbox={renderCheckbox}
                />
            </TripSheetSection>

            <TripSheetSection title="Работа ТС">
                {renderDateField(plannedDepartureAt)}
                {renderDateField(actualDepartureAt)}
                {renderField(odometerValueOnDeparture)}
                <ReadonlyRow
                    label="Источник одометра при выезде"
                    value={enumLabel(tripSheetFactSources, data.departureOdometerSource)}
                />
                <ReadonlyRow
                    label="Время фиксации одометра при выезде"
                    value={formatAssignmentDate(data.departureOdometerFixedAt, organizationTimezone)}
                />
                {data.departureOdometerSource === tripSheetFactSourcesEnum.Corrected &&
                    renderField(departureOdometerCorrectionReason)}
                {renderDateField(plannedReturnAt)}
                {renderDateField(actualReturnAt)}
                {renderField(odometerValueOnReturn)}
                <ReadonlyRow
                    label="Источник одометра при возвращении"
                    value={enumLabel(tripSheetFactSources, data.returnOdometerSource)}
                />
                <ReadonlyRow
                    label="Время фиксации одометра при возвращении"
                    value={formatAssignmentDate(data.returnOdometerFixedAt, organizationTimezone)}
                />
                {data.returnOdometerSource === tripSheetFactSourcesEnum.Corrected &&
                    renderField(returnOdometerCorrectionReason)}
                {renderField(mileageDiscrepancyReason)}
                {data.actualMileage !== null && data.actualMileage !== undefined ? (
                    <ReadonlyRow label="Общий фактический пробег" value={data.actualMileage} />
                ) : null}
            </TripSheetSection>

            <TripSheetSection title="Движение горючего">
                {renderField(issuedLiters)}
                <ReadonlyRow
                    label="Источник выданного горючего"
                    value={enumLabel(tripSheetFactSources, data.fuelInfoData?.issuedSource)}
                />
                {data.fuelInfoData?.issuedSource === tripSheetFactSourcesEnum.Corrected &&
                    renderField(issuedCorrectionReason)}
                {renderField(balanceDepartureLiters)}
                <ReadonlyRow
                    label="Источник остатка при выезде"
                    value={enumLabel(tripSheetFactSources, data.fuelInfoData?.balanceDepartureSource)}
                />
                {data.fuelInfoData?.balanceDepartureSource === tripSheetFactSourcesEnum.Corrected &&
                    renderField(balanceDepartureCorrectionReason)}
                {renderField(refilledOnRouteLiters)}
                <ReadonlyRow
                    label="Источник заправленного в пути"
                    value={enumLabel(tripSheetFactSources, data.fuelInfoData?.refilledOnRouteSource)}
                />
                {data.fuelInfoData?.refilledOnRouteSource === tripSheetFactSourcesEnum.Corrected &&
                    renderField(refilledOnRouteCorrectionReason)}
                {renderField(balanceReturnLiters)}
                <ReadonlyRow
                    label="Источник остатка при возвращении"
                    value={enumLabel(tripSheetFactSources, data.fuelInfoData?.balanceReturnSource)}
                />
                {data.fuelInfoData?.balanceReturnSource === tripSheetFactSourcesEnum.Corrected &&
                    renderField(balanceReturnCorrectionReason)}
                {renderField(normChangeCoefficient)}
                <ReadonlyRow
                    label="Фактический расход, л"
                    value={
                        fuelConsumption === null
                            ? "Не рассчитан: заполнены не все показатели движения горючего"
                            : fuelConsumption
                    }
                />
            </TripSheetSection>

            <TripSheetSection title="Ответственные">
                {renderField(mechanicName)}
                {renderField(dispatcherName)}
                {renderField(taxerName)}
                {renderField(controllerName)}
            </TripSheetSection>

            <TripSheetSection title="Осмотры">
                <TechnicalInspectionsSection
                    inspections={technicalInspections}
                    onChange={onTechnicalInspectionsChange}
                    canEditFacts={canEditFacts}
                    renderField={renderField}
                    renderSelect={renderSelect}
                    renderDateField={renderDateField}
                />
                {renderCheckbox(technicalInspectionConfirmedOnPaper)}
                {renderCheckbox(medicalMarksConfirmedOnPaper)}
                {(data.preTripInspectionAt || data.medicalExamAt || data.medicalInspectionAt) && (
                    <div className="alert alert-light border py-2 mt-3">
                        Исторические поля старого формата сохранены для миграционной совместимости.
                    </div>
                )}
                <ReadonlyRow
                    label={preTripInspectionAt.label}
                    value={formatAssignmentDate(data.preTripInspectionAt, organizationTimezone)}
                />
                <ReadonlyRow label={medicalWorkerName.label} value={data.medicalWorkerName} />
                <ReadonlyRow
                    label={medicalExamAt.label}
                    value={formatAssignmentDate(data.medicalExamAt, organizationTimezone)}
                />
            </TripSheetSection>

            {!!Button && <Button />}
        </div>
    );
};
export default Markup;
